import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { NewModal, RadioInput } from 'lib/components';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { ConfirmDelete } from './confirmationModals/ConfirmDelete';
import { Button } from 'react-covideo-common';
import { Folder } from 'lib/api/folders/getFolders';
import {
  ACCESS_FILTER,
  FolderV2Dropdown,
} from 'lib/components/dropdown/folders/FolderV2Dropdown';
import { DEFAULT_FOLDER } from 'lib/const/Folders';

const ContentFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
`;

const RadioText = styled.div`
  font-weight: 400;
  font-size: 16px;
  margin: 0;
`;

type Props = {
  handleModalClose: () => void;
  onSubmit: (data: any) => void;
  currentFolder?: Folder;
};

export const ModalDeleteFolder = ({
  handleModalClose,
  onSubmit,
  currentFolder,
}: Props) => {
  const { t } = useTranslation();
  const [displayConfirmationModalBody, setDisplayConfirmationModalBody] =
    useState<boolean>(false);
  const themes = useTheme();

  const hideConfirmationModalBody = () => {
    setDisplayConfirmationModalBody(false);
  };

  const showConfirmationModalBody = () => {
    setDisplayConfirmationModalBody(true);
  };

  return (
    <NewModal
      headerText={
        displayConfirmationModalBody
          ? t('Delete folder and videos')
          : t('Delete folder "{{folderName}}"', {
              folderName: currentFolder?.name,
            })
      }
      headerTextStyle={{
        fontSize: displayConfirmationModalBody ? '24px' : '20px',
      }}
      hideCloseButtonInHeader={true}
      closeModalOnBackdropOrEscPress={false}
      closeModal={handleModalClose}
    >
      <Formik
        initialValues={{
          delete: true,
          folder: { value: -2, label: t('Primary') },
        }}
        onSubmit={async values => {
          await onSubmit(values);
          hideConfirmationModalBody();
        }}
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <>
            {!displayConfirmationModalBody && (
              <>
                <div>
                  <RadioInput
                    name={'delete'}
                    value={'#FFFFFF'}
                    text={
                      <RadioText>
                        <b>{t('Delete')}</b> {t('all videos and the folder')}
                      </RadioText>
                    }
                    checkedColor={themes.colors.primary[100] || '#FF8B22'}
                    checked={values.delete}
                    onChange={() => {
                      setFieldValue('delete', true);
                    }}
                    onClick={() => setFieldValue('delete', true)}
                  />
                  <RadioInput
                    name={'delete'}
                    value={'#FFFFFF'}
                    text={
                      <RadioText>
                        <b>{t('Move')}</b> {t('all videos to another folder')}
                      </RadioText>
                    }
                    checkedColor={themes.colors.primary[100] || '#FF8B22'}
                    checked={!values.delete}
                    onChange={() => {
                      setFieldValue('delete', false);
                    }}
                    onClick={() => setFieldValue('delete', false)}
                  />
                </div>
                {!values.delete && (
                  <FolderV2Dropdown
                    showAllFolders={false}
                    showCreateButton={false}
                    accessFilter={ACCESS_FILTER.EDIT}
                    dropdownHeight={200}
                    containerWidth='100%'
                    onChange={option => setFieldValue('folder', option)}
                    getShared={false}
                    folderToDelete={Number(currentFolder?.folderId) || 0}
                    value={{
                      value: values.folder?.value ?? DEFAULT_FOLDER.folderId,
                      label: values.folder?.label ?? DEFAULT_FOLDER.name,
                    }}
                    fetchFolders={true}
                  />
                )}
                <ContentFooter>
                  <Button
                    text={t('Cancel')}
                    variant='secondary'
                    onClick={handleModalClose}
                  />
                  <Button
                    text={
                      !values.delete
                        ? t('Move Videos & Delete Folder')
                        : t('Delete folder')
                    }
                    disabled={
                      isSubmitting || (!values.delete && !values.folder)
                    }
                    variant={!values.delete ? 'primary' : 'red'}
                    onClick={showConfirmationModalBody}
                  />
                </ContentFooter>
              </>
            )}
            {displayConfirmationModalBody && (
              <ConfirmDelete
                buttonText={
                  values.delete
                    ? t('Yes, Delete Folder and All Videos')
                    : t('Yes, Delete Folder and Move All Videos')
                }
                message={
                  values.delete
                    ? t('delete the folder and all videos in it?')
                    : t(
                        'delete the folder and move all videos to {{folderLabel}} folder?',
                        { folderLabel: values.folder.label }
                      )
                }
                handleModalClose={hideConfirmationModalBody}
              />
            )}
          </>
        )}
      </Formik>
    </NewModal>
  );
};
